import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'https://welfareops-api.valleyintest.workers.dev';

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options: FetchOptions = {}): Promise<T> {
    const { skipAuth = false, ...fetchOptions } = options;
    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (!skipAuth) {
      const token = useAuthStore.getState().accessToken;
      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    if (response.status === 401 && !skipAuth) {
      // トークンリフレッシュを試行
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // リトライ
        const newToken = useAuthStore.getState().accessToken;
        (headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
        const retryResponse = await fetch(url, { ...fetchOptions, headers });
        if (!retryResponse.ok) {
          throw new Error(`API Error: ${retryResponse.status}`);
        }
        return retryResponse.json();
      } else {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        throw new Error('Session expired');
      }
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(error.message || error.error || `API Error: ${response.status}`);
    }

    return response.json();
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      useAuthStore.getState().setAccessToken(data.accessToken);
      return true;
    } catch {
      return false;
    }
  }

  async get<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async put<T>(endpoint: string, body?: unknown, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: FetchOptions): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const api = new ApiClient(API_URL);

// 認証API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{
      accessToken: string;
      refreshToken: string;
      user: {
        id: string;
        email: string;
        name: string;
        role: string;
        tenantId: string;
        facilityId: string;
      };
    }>('/api/v1/auth/login', { email, password }, { skipAuth: true }),
  logout: () => api.post('/api/v1/auth/logout'),
  me: () => api.get('/api/v1/auth/me'),
};

// クライアントAPI
export const clientsApi = {
  list: (params?: { page?: number; limit?: number; search?: string; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    if (params?.status) query.set('status', params.status);
    return api.get(`/api/v1/clients?${query}`);
  },
  get: (id: string) => api.get(`/api/v1/clients/${id}`),
  create: (data: unknown) => api.post('/api/v1/clients', data),
  update: (id: string, data: unknown) => api.put(`/api/v1/clients/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/clients/${id}`),
};

// 職員API
export const staffApi = {
  list: (params?: { page?: number; limit?: number; search?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.search) query.set('search', params.search);
    return api.get(`/api/v1/staff?${query}`);
  },
  get: (id: string) => api.get(`/api/v1/staff/${id}`),
  create: (data: unknown) => api.post('/api/v1/staff', data),
  update: (id: string, data: unknown) => api.put(`/api/v1/staff/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/staff/${id}`),
};

// 出欠API
export const attendancesApi = {
  list: (params?: { date?: string; startDate?: string; endDate?: string; clientId?: string }) => {
    const query = new URLSearchParams();
    if (params?.date) query.set('date', params.date);
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.clientId) query.set('clientId', params.clientId);
    return api.get(`/api/v1/attendances?${query}`);
  },
  create: (data: unknown) => api.post('/api/v1/attendances', data),
  bulkCreate: (data: unknown) => api.post('/api/v1/attendances/bulk', data),
  update: (id: string, data: unknown) => api.put(`/api/v1/attendances/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/attendances/${id}`),
};

// 個別支援計画API
export const supportPlansApi = {
  list: (params?: { page?: number; limit?: number; status?: string }) => {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.status) query.set('status', params.status);
    return api.get(`/api/v1/support-plans?${query}`);
  },
  get: (id: string) => api.get(`/api/v1/support-plans/${id}`),
  create: (data: unknown) => api.post('/api/v1/support-plans', data),
  update: (id: string, data: unknown) => api.put(`/api/v1/support-plans/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/support-plans/${id}`),
};

// サービス提供記録API
export const serviceRecordsApi = {
  list: (params?: { startDate?: string; endDate?: string; clientId?: string }) => {
    const query = new URLSearchParams();
    if (params?.startDate) query.set('startDate', params.startDate);
    if (params?.endDate) query.set('endDate', params.endDate);
    if (params?.clientId) query.set('clientId', params.clientId);
    return api.get(`/api/v1/service-records?${query}`);
  },
  get: (id: string) => api.get(`/api/v1/service-records/${id}`),
  create: (data: unknown) => api.post('/api/v1/service-records', data),
  update: (id: string, data: unknown) => api.put(`/api/v1/service-records/${id}`, data),
  delete: (id: string) => api.delete(`/api/v1/service-records/${id}`),
};

// ダッシュボードAPI
export const dashboardApi = {
  stats: () => api.get('/api/v1/dashboard/stats'),
  recentAttendances: () => api.get('/api/v1/dashboard/recent-attendances'),
  alerts: () => api.get('/api/v1/dashboard/alerts'),
};


